import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  Home,
  CheckCircle,
  Star,
  Ruler,
  Shield,
  Palette,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FittedWardrobesPage = () => {
  const wardrobeStyles = [
    {
      name: 'Simplicity Style',
      subtitle: 'Clean & Minimal',
      description: 'Embrace "less is more" with our Simplicity style. Clean lines and defined looks create dramatic, chic fitted wardrobes that do all the talking.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/3ad24081315c82e8441c5431ac32334c66635adc8d92beaa733c307ff91165fe.png',
      features: [
        'Handleless design options',
        'Clean minimal lines',
        'Alpine White & Natural Grey finishes',
        'Dust Grey & Reed Green available',
        'White Oak Effect doors',
        'Maximized storage space'
      ],
      priceRange: 'From £1,200',
      color: 'text-[#8CB89F]'
    },
    {
      name: 'Shaker Style',
      subtitle: 'Timeless Elegance',
      description: 'The enduringly popular Shaker style brings simple, refined, versatile design. A stunning match for both traditional and contemporary bedrooms.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      features: [
        'Traditional panel design',
        'Multiple panel configurations',
        'Soft Cream & Cashmere finishes',
        'Tyrolean Blue & Reed Green',
        'Grey Oak & Honey Castello Oak',
        'Classic proportions'
      ],
      priceRange: 'From £1,450',
      color: 'text-green-600'
    },
    {
      name: 'Farmhouse Style',
      subtitle: 'Country Character',
      description: 'Classic country living style with welcoming personality. Our Farmhouse brings rustic charm with versatile and timeless appeal.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Country-style detailing',
        'Rustic character elements',
        'Natural Grey & Dust Grey',
        'Soft Cream finishes',
        'Grey Oak Effect available',
        'Paintable options'
      ],
      priceRange: 'From £1,350',
      color: 'text-amber-600'
    }
  ];

  const wardrobeBenefits = [
    {
      icon: Ruler,
      title: 'Made to Measure',
      description: 'Every wardrobe perfectly fitted to your space, from floor to ceiling'
    },
    {
      icon: Palette,
      title: 'Endless Finishes',
      description: 'Choose from 13+ colors and finishes including paintable options'
    },
    {
      icon: Shield,
      title: 'Premium Quality',
      description: 'Engineered construction with 18mm thick boards and soft-close hinges'
    },
    {
      icon: Star,
      title: 'Professional Design',
      description: 'Free consultation with our expert fitted furniture designers'
    }
  ];

  const finishOptions = [
    { name: 'Alpine White', color: '#FFFFFF', description: 'Pure white finish' },
    { name: 'Natural Grey', color: '#9B9B9B', description: 'Sophisticated grey' },
    { name: 'Dust Grey', color: '#D5D5D5', description: 'Light contemporary grey' },
    { name: 'Soft Cream', color: '#F5F5DC', description: 'Warm cream tone' },
    { name: 'Cashmere', color: '#E8E0D4', description: 'Elegant neutral' },
    { name: 'Reed Green', color: '#7A8B7A', description: 'Natural green shade' },
    { name: 'Tyrolean Blue', color: '#4A6B8A', description: 'Deep blue accent' },
    { name: 'White Oak Effect', color: '#F4E9D9', description: 'Light wood grain' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <Home className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Bespoke Storage</Badge>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Fitted Wardrobes
                <span className="block" style={{color: '#8CB89F'}}>Made to Measure</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <strong>Aston Lily Joinery</strong> creates stunning made-to-measure fitted wardrobes that maximize every inch of your bedroom space. From classic Shaker to modern Simplicity styles.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Wardrobe Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Free Design Consultation
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/3ad24081315c82e8441c5431ac32334c66635adc8d92beaa733c307ff91165fe.png"
                alt="Fitted Wardrobes"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Aston Lily Fitted Wardrobes?</h2>
            <p className="text-xl text-gray-600">
              Bespoke design, premium quality, endless possibilities
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {wardrobeBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Wardrobe Styles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Wardrobe Styles</h2>
            <p className="text-xl text-gray-600">
              Choose from our range of fitted wardrobe door styles
            </p>
          </div>
          
          <div className="space-y-20">
            {wardrobeStyles.map((style, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img 
                    src={style.image} 
                    alt={style.name}
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className={style.color}>{style.subtitle}</Badge>
                  </div>
                  
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">{style.name}</h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{style.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {style.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#8CB89F]" />
                      <span className="font-semibold text-lg">{style.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finish Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Finish Options</h2>
            <p className="text-xl text-gray-600">
              Choose from our range of colors and finishes
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {finishOptions.map((finish, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-4 text-center">
                  <div 
                    className="w-full h-16 rounded-lg mb-3 border"
                    style={{backgroundColor: finish.color}}
                  ></div>
                  <h4 className="font-semibold text-xs mb-1">{finish.name}</h4>
                  <p className="text-xs text-gray-600">{finish.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Transform Your Bedroom Storage</h2>
          <p className="text-xl mb-8 opacity-90">
            Get a free design consultation and quote for your bespoke fitted wardrobes from Aston Lily Joinery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Fitted Wardrobe Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Book Design Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FittedWardrobesPage;